import { useEffect, useRef, useState } from "react";

import type { AssistantConversation } from "../api/types";

function stamp(conversation: AssistantConversation): string {
  return new Date(conversation.last_message_at ?? conversation.created_at).toLocaleString(
    "de-DE",
    { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" },
  );
}

/**
 * Conversation picker. A native select cannot carry a delete button per row,
 * so this is a small popover: one line per thread with its title and time, a
 * cross to delete it, and "+ Neu" at the bottom.
 */
export function ConversationMenu({
  conversations,
  currentId,
  disabled,
  onSelect,
  onDelete,
}: {
  conversations: AssistantConversation[];
  currentId: string | null;
  disabled?: boolean;
  onSelect: (id: string | null) => void;
  onDelete: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = conversations.find((c) => c.id === currentId) ?? null;

  return (
    <div className="conv-menu" ref={boxRef}>
      <button
        className="conv-trigger"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((value) => !value)}
        title={current ? current.title : "Neue Unterhaltung"}
      >
        <span className="conv-trigger-label">{current ? current.title : "Neue Unterhaltung"}</span>
        <span className="conv-caret">{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div className="conv-panel" role="listbox">
          <div className="conv-list">
            {conversations.length === 0 && (
              <div className="conv-empty">Noch keine Unterhaltungen</div>
            )}
            {conversations.map((conversation) => (
              <div
                key={conversation.id}
                className={"conv-item" + (conversation.id === currentId ? " current" : "")}
                role="option"
                aria-selected={conversation.id === currentId}
                tabIndex={0}
                onClick={() => {
                  onSelect(conversation.id);
                  setOpen(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(conversation.id);
                    setOpen(false);
                  }
                }}
              >
                <span className="conv-item-text">
                  <span className="conv-item-title">{conversation.title}</span>
                  <span className="conv-item-stamp">
                    {stamp(conversation)}
                  </span>
                </span>
                <button
                  className="conv-delete"
                  title="Unterhaltung löschen"
                  aria-label={`Unterhaltung „${conversation.title}" löschen`}
                  onClick={(event) => {
                    // The row underneath would otherwise switch to the thread
                    // that is about to disappear.
                    event.stopPropagation();
                    onDelete(conversation.id);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <button
            className="conv-new"
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
          >
            <span className="conv-plus">+</span> Neuer Chat
          </button>
        </div>
      )}
    </div>
  );
}
